<template>
	<div class="new-invoice-container">
	    <ModalHeader :title="getString('new_invoice')" :button-text="buttonText" @click-button="handleMainButtonClick" :button-loading="loadingInvoice" :button-disabled="buttonDisabled"></ModalHeader>
		<AppList :items="listItems">
			<template v-slot:button="{item}">
				<div class="row centered space-between p-10">
					<p class="list-item-title">{{ item.title }}</p>
					<AppButton small outline :text="item.title" @click="item.callback()" :disabled="item.buttonDisabled"/>
				</div>
			</template>
			<template #items-list="{item}">
				<!--	Add items section			-->
				<div class="items-container">
					<!-- Toggle switch -->
					<div class="section-toggle">
						<AppTabsSwitch
							auto-switch
							:items="[getString('shortcuts'), getString('new')]"
							:active-item="activeSection === 'shortcuts' ? 0 : 1"
							@switched="index => activeSection = index === 0 ? 'shortcuts' : 'newItem'" 
						/>
					</div>

					<!-- Shortcuts section -->
					<div v-show="activeSection === 'shortcuts'" class="shortcuts-section">
						<div class="categories-tabs">
							<div class="category-tab" 
								v-for="(items, category) in shortcuts" 
								:key="category"
								:class="{ active: selectedCategory === category }"
								@click="selectedCategory = category">
								{{ getCategoryLabel(category) }}
							</div>
						</div>
						<div class="shortcuts-container">
							<div class="shortcuts-wrapper">
								<div class="shortcut" v-for="(shortcut, index) in shortcuts[selectedCategory]" :key="`${selectedCategory}-${index}`" @click="addItem(shortcut)">
									<div class="shortcut-title">{{ shortcut.title }}</div>
									<div class="shortcut-price">{{ readableEurValue(shortcut.price) }}</div>
								</div>
							</div>
						</div>
					</div>

					<!-- New item section -->
					<div v-show="activeSection === 'newItem'" class="new-item-container">
						<div class="form-row">
							<div class="left-column">
								<AppTextInput class="new-item-title" :label="getString('name')" :placeholder="getString('new_item')" :value="newItemTitle"
											  @changed-text="v => newItemTitle = v"/>
								<AppTextInput textarea class="new-item-description" :label="getString('description')" :placeholder="getString('item_description_optional')"
											  :value="newItemDescription"
											  @changed-text="v => newItemDescription = v"/>
							</div>
							<div class="right-column">
								<AppNumberInput :placeholder="getString('price')" :label="getString('price')" symbol="€" :right-side-symbol="true"
												:min="0" :value="newItemPrice" @changed-value="v => newItemPrice = v"/>
								<AppNumberInput :placeholder="getString('discount')" :label="getString('discount')" symbol="%" :right-side-symbol="true"
												:min="0" :max="100" :value="newItemDiscount || 0" @changed-value="v => newItemDiscount = v"/>
								<AppNumberInput :placeholder="getString('quantity')" :label="getString('quantity')" :min="1" :value="newItemQuantity"
												@changed-value="v => newItemQuantity = v"/>
							</div>
						</div>
						<AppButton class="add-button" :text="getString('add')" @click="addNewItem" :disabled="!newItemTitle || !newItemPrice"/>
					</div>
					<div class="items-list">
						<div class="item" v-for="(item, index) in items" :key="item.id">
							<div class="info">
								<div class="item-title">{{ item.title }}</div>
								<div class="item-description" v-if="item.description">{{ item.description }}</div>
								<div class="item-price">{{ readableEurValue(item.price) }}</div>
							</div>
							<div class="controls">
								<div class="control-item">
									<label class="control-label">{{ getString('discount') }}</label>
									<AppNumberInput :min="0" :max="100" :value="item.discount || 0" symbol="%" :right-side-symbol="true" @changed-value="v => changeDiscount(item, v)"/>
								</div>
								<div class="control-item">
									<label class="control-label">{{ getString('quantity') }}</label>
									<AppNumberInput :min="0" :value="item.quantity" @changed-value="v => changeQuantity(item, v)"/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</AppList>
		<AppNotification ref="success-generic" :title="getString('success')" :body-text="getString('operation_executed')" :type="1"/>
		<AppNotification ref="error-generic" :title="getString('error')" :body-text="getString('error_generic')" :type="-2"/>
		<AppNotification ref="error-no-object" :title="getString('error')" :body-text="getString('no_object')" :type="-2"/>
		<AppNotification ref="error-no-items" :title="getString('error')" :body-text="getString('quote_error_no_items')" :type="-2"/>
		<AppNotification ref="error-stripe" :title="getString('error')" :body-text="getString('quote_error_stripe_error')" :type="-2"/>
		<AppNotification ref="error-missing-data" :title="getString('error')" :body-text="getString('quote_error_missing_data')" :type="-2"/>
		<AppPopup
			:title="popupInvoiceTitle"
			:body-text="popupInvoiceBody"
			:buttons="[{text: getString('ok'), callback: callPopupAction}]"
			ref="popup-invoice">
			<a class="mb-10" :href="popupInvoiceLink" target="_blank">{{ popupInvoiceLink }}</a>
			<p class="mb-10" v-if="!paid">
				{{ getString('pay_link_or_qr') }}
			</p>
			<AppQrImage :value="popupInvoiceLink"/>
		</AppPopup>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="quotePreviewActive" @toggling-popup="v => quotePreviewActive = v">
			<ModalHeader :title="getString('quote_preview')" :button-text="getString('download')" @click-button="downloadQuotePdf">
				<template #extra-buttons>
					<AppButton outline :text="getString('copy_payment_link')" @click="copyPaymentLink"/>
				</template>
			</ModalHeader>
			<div v-if="regeneratingPaymentLink" class="loader-container">
				<AppLoader :size="48"/>
				<p class="loader-text">{{ getString('updating_payment_link') }}</p>
			</div>
			<QuotePreview v-else-if="quotePreviewActive" :key="quotePreviewKey" :number="currentQuoteId || editingQuote?.quote_id || 1" :year="new Date().getFullYear()"
						  :date="formatDateYYMMDD(new Date())"
						  :quote-link="popupInvoiceLink" :recipient="business?.name || 'Destinatario non specificato'" :subject="object"
						  :items="items" @generated="doc => quotePdfDoc = doc"/>
		</AppBottomSheet>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useUserStore} from "@/user/stores";
import {useLanguageStore, useScriptStore, useCurrencyStore} from "@/shared/stores";
import {formatDateTime, formatDateYYMMDD} from "@/shared/helpers/dates";
import AppList from "@/shared/components/AppList.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import {getRandomAlphanumericString} from "@/shared/helpers/strings";
import API from "@/shared/helpers/API";
import AppNotification from "@/shared/components/AppNotification.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import QuotePreview from "@/user/components/super/QuotePreview.vue";
import AppQrImage from "@/shared/components/AppQrImage.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const scriptStore = useScriptStore();
		const currencyStore = useCurrencyStore();
		return {
			userStore,
			formatDateTime,
			formatDateYYMMDD,
			getString: languageStore.getString,
			readableEurValue: languageStore.readableEurValue,
			currencyStore,
			languageStore,
			scriptStore
		}
	},
	name: "NewInvoice",
       components: {AppPopup, AppNotification, AppNumberInput, AppTextInput, AppButton, AppList, ModalHeader, AppBottomSheet, QuotePreview, AppQrImage, AppLoader, AppTabsSwitch},
	mixins: [WindowWidth],
	emits: ['close'],
	data() {
		return {
			paid: false,
			object: '',
			items: [],
			newItemQuantity: 1,
			newItemPrice: 0,
			newItemTitle: '',
			newItemDescription: '',
			newItemDiscount: 0,
			popupInvoiceTitle: '',
			popupInvoiceBody: '',
			popupInvoiceLink: '',
			popupAction: null,
			loadingInvoice: false,
			quotePreviewActive: false,
			quotePdfDoc: null,
			autoSaveTimeout: null,
			regeneratingPaymentLink: false,
			hasPendingChanges: false,
			quotePreviewKey: 0,
			selectedCategory: 'pos',
			currentQuoteId: null,
			activeSection: 'shortcuts', // 'shortcuts' or 'newItem'
		}
	},
	props: {
		business: {type: Object, required: true},
		editingQuote: {type: Object, default: null},
	},
	computed: {
		shortcuts() {
			return {
				pos: [
					{
						id: 1,
						title: "Qromo POS",
						price: 199,
					},
					{
						id: 2,
						title: "Qromo POS Mini",
						price: 60,
					},
					{
						id: 3,
						title: "Qromo POS S700",
						price: 399,
					}
				],
				devices: [
					{
						id: 4,
						title: "Qromo Printer",
						price: 249,
					},
					{
						id: 5,
						title: "Palmare Android",
						price: 229,
					},
					{
						id: 6,
						title: "Tablet Android",
						price: 249,
					},
					{
						id: 7,
						title: "Qromo Tower",
						price: 1099,
					},
				],
				epson: [
					{
						id: 8,
						title: "Epson FP 81-RT",
						price: 799,
					},
					{
						id: 9,
						title: "Scontrino Smart Epson",
						price: 99,
					},
					{
						id: 12,
						title: "Cassetto Porta Monete",
						price: 99,
					}
				],
				services: [
					{
						id: -1,
						title: "Spedizione standard",
						price: 10,
					},
					{
						id: -2,
						title: "Spedizione express",
						price: 20,
					},
					{
						id: -3,
						title: "Assistenza pre-live",
						price: 500,
						defaultDiscount: 100,
						description: "Supporto completo per configurazione iniziale",
					},
					{
						id: -4,
						title: "Setup Account Pre-Live",
						price: 500,
						defaultDiscount: 100,
						description: "Configurazione account e formazione base",
					}
				],
				qrcode: [
					{
						id: 10,
						title: "QR CODE WOOD XL 1",
						price: 7,
					},
					{
						id: 11,
						title: "QR CODE FOREX",
						price: 25,
					}
				]
			}
		},
listItems() {
			let items = [];
			items.push(
				{
					title: this.getString('already_paid'),
					switch: true,
					active: this.paid,
					callback: (value) => {
						this.paid = value;
					},
					explain: this.paid ? this.getString('invoice_explain') : this.getString('quote_explain'),
				},
				{
					title: this.getString('object'),
					textInput: true,
					isTextArea: true,
					placeholder: this.getString('object'),
					value: this.object,
					callbackText: (value) => {
						this.object = value;
					},
				},
				{
					title: this.getString('items'),
					customSlot: 'items-list',
					explain: this.getString('all_items_vat_excluded'),
				}
			);

			return items;
		},
		total() {
			return this.items.reduce((acc, item) => {
				const subtotal = item.price * item.quantity; // No VAT added
				const discountAmount = subtotal * (item.discount || 0) / 100;
				return acc + subtotal - discountAmount;
			}, 0);
		},
		buttonText() {
			return this.total > 0 ? this.readableEurValue(this.total) : null;
		},
		buttonDisabled() {
			return this.items.length === 0;
		},
	   bottomSheetWidth() {
                       if (this.windowWidth < 550) {
                               return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		},
	},
	watch: {
		editingQuote: {
			handler(newQuote) {
				if (newQuote) {
					this.loadQuoteData(newQuote);
				} else {
					this.resetForm();
				}
			},
			immediate: true
		},
		quotePreviewActive: {
			handler(newValue, oldValue) {
				// If preview just opened and we have pending changes from before, regenerate payment link
				if (newValue && !oldValue && this.hasPendingChanges && this.editingQuote) {
					this.hasPendingChanges = false;
					this.$nextTick(() => {
						this.regeneratePaymentLink();
					});
				}
			}
		},
	},
	methods: {
		getCategoryLabel(category) {
			const labels = {
				services: 'Servizi',
				pos: 'POS',
				devices: 'Dispositivi',
				epson: 'Epson',
				qrcode: 'QR Code'
			};
			return labels[category] || category;
		},
		handleMainButtonClick() {
			if (!this.paid) {
				// For quotes: show preview instead of saving
				this.createQuoteAndShowPreview();
			} else {
				// For invoices: save directly
				this.emitInvoice();
			}
		},
		loadQuoteData(quote) {
			
			// Load basic quote info
			this.object = quote.object || 'Preventivo Modificato';
			this.paid = quote.status === 'paid';
			this.currentQuoteId = quote.quote_id;
			
			// Load quote items and convert them to the right format
			this.items = quote.items.map(item => ({
				id: item.quote_item_id,
				title: item.name,
				description: item.details || '',
				price: this.currencyStore.dbToActualValue(item.amount), // Database is in cents, frontend is in euros
				quantity: item.quantity,
				discount: item.discount || 0
			}));
		},
		resetForm() {
			this.object = '';
			this.items = [];
			this.paid = false;
			this.newItemTitle = '';
			this.newItemDescription = '';
			this.newItemPrice = 0;
			this.newItemQuantity = 1;
			this.newItemDiscount = 0;
			this.currentQuoteId = null;
		},
		closeDelay() {
			setTimeout(() => {
				this.$emit('close');
			}, 300);
		},
		callPopupAction() {
			if (this.popupAction) {
				this.popupAction();
			}
			this.$refs['popup-invoice'].toggleStatus(false);
		},
		changeQuantity(item, value) {
			if (value === 0) {
				this.items = this.items.filter(i => i !== item);
			} else {
				item.quantity = value;
			}
			this.handleItemChange();
		},
		changeDiscount(item, value) {
			item.discount = Math.min(100, Math.max(0, value || 0));
			this.handleItemChange();
		},
		addItem(itemToAdd) {
			const item = this.items.find(i => i.id === itemToAdd.id);
			if (item) {
				item.quantity++;
			} else {
				// Use defaultDiscount if available, otherwise 0
				const discount = itemToAdd.defaultDiscount || 0;
				this.items.push(Object.assign({}, itemToAdd, {quantity: 1, discount: discount}));
			}
			this.handleItemChange();
		},
		addNewItem() {
			if (this.newItemTitle && this.newItemPrice) {
				this.items.push({
					id: getRandomAlphanumericString(10),
					title: this.newItemTitle,
					description: this.newItemDescription,
					price: this.newItemPrice, // Keep in euros
					quantity: this.newItemQuantity,
					discount: this.newItemDiscount || 0,
				});
				this.newItemTitle = '';
				this.newItemDescription = '';
				this.newItemPrice = 0;
				this.newItemQuantity = 1;
				this.newItemDiscount = 0;
				this.handleItemChange();
			}
		},
		createQuoteAndShowPreview() {
			if (this.items.length === 0) {
				this.$refs['error-no-items'].openNotification();
				return;
			}
			
			if (!this.paid) {
				// For unpaid quotes: show preview first, then save
				this.showPreviewBeforeSaving();
			} else {
				// For paid invoices: use original logic
				this.emitInvoice();
			}
		},
		showPreviewBeforeSaving() {
			if (this.editingQuote) {
				// Editing existing quote: generate new payment link for preview without saving
				this.generatePaymentLinkForPreview();
			} else {
				// Creating new quote: save first to get payment link, then show preview
				this.saveQuoteInBackground();
			}
		},
		generatePaymentLinkForPreview() {
			if (this.items.length === 0) {
				this.$refs['error-no-items'].openNotification();
				return;
			}
			
			// For editing existing quotes: show preview first, then auto-save
			this.popupInvoiceLink = this.editingQuote?.payment_link || '';
			this.quotePreviewActive = true;
			
			// Auto-save after showing preview
			this.$nextTick(() => {
				setTimeout(() => {
					this.saveQuoteInBackground();
				}, 1000); // 1 second delay to let user see the preview first
			});
		},
		saveQuoteInBackground() {
			// Different behavior for new vs edited quotes
			if (this.editingQuote) {
				// For edited quotes: save in background without closing preview
				this.saveQuoteInBackgroundOnly();
			} else {
				// For new quotes: save and show preview with payment link
				this.saveQuoteAndShowPreview();
			}
		},
		saveQuoteInBackgroundOnly() {
			if (this.items.length === 0) {
				this.$refs['error-no-items'].openNotification();
				return;
			}
			
			const quoteId = this.editingQuote?.quote_id || null;
			
			if (quoteId) {
				// Save changes in background without closing preview
				let itemsToSend = this.items.map(i => ({
					description: i.title,
					details: i.description || '',
					price: Math.round(i.price * 100), // Convert euros to cents for backend
					quantity: i.quantity,
					discount: i.discount || 0,
				}));
				
				const objectToSend = (this.object && this.object.length > 2) ? this.object : 'Preventivo Modificato';
				
				// Update the frontend object value if it's empty
				if (!this.object || this.object.length <= 2) {
					this.object = 'Preventivo Modificato';
				}

				API.init().superEmitInvoice(
					this.business?.business_id || 0, 
					0, // paid = 0 for quote
					objectToSend, 
					JSON.stringify(itemsToSend), 
					(response) => {},
					true, // force_incomplete flag
					quoteId // quote_id for update
				);
			}
		},
		downloadQuotePdf() {
			if (this.quotePdfDoc) {
				const quoteNumber = this.currentQuoteId || this.editingQuote?.quote_id || 1;
				const businessName = this.business?.name || 'cliente';
				const date = this.formatDateYYMMDD(new Date());
				// Clean business name for filename (remove special characters)
				const cleanBusinessName = businessName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
				const filename = `preventivo_${quoteNumber}_${cleanBusinessName}_${date}.pdf`;
				this.quotePdfDoc.save(filename);
			}
		},
		copyPaymentLink() {
			if (this.popupInvoiceLink) {
				try {
					// Try modern clipboard API first
					if (navigator.clipboard && navigator.clipboard.writeText) {
						navigator.clipboard.writeText(this.popupInvoiceLink).then(() => {
							this.$refs['success-generic'].openNotification();
						}).catch(() => {
							this.fallbackCopyToClipboard();
						});
					} else {
						this.fallbackCopyToClipboard();
					}
				} catch (err) {
					this.fallbackCopyToClipboard();
				}
			}
		},
		fallbackCopyToClipboard() {
			// Fallback method for copying to clipboard
			const textArea = document.createElement('textarea');
			textArea.value = this.popupInvoiceLink;
			textArea.style.position = 'fixed';
			textArea.style.left = '-999999px';
			textArea.style.top = '-999999px';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			
			try {
				const successful = document.execCommand('copy');
				if (successful) {
					this.$refs['success-generic'].openNotification();
				}
			} catch (err) {
				console.error('Fallback: Copying text command was unsuccessful', err);
			}
			
			document.body.removeChild(textArea);
		},
		handleItemChange() {
			// Check first condition explicitly
			const hasQuote = !!this.editingQuote;
			const previewNotActive = !this.quotePreviewActive;
			
			// If we have an existing quote but preview is not active, mark as pending changes
			if (this.editingQuote && !this.quotePreviewActive) {
				this.hasPendingChanges = true;
				return;
			}
			
			// Only regenerate payment link if we're in preview mode and have an existing quote
			if (this.quotePreviewActive && this.editingQuote && !this.regeneratingPaymentLink) {
				
				// Clear any existing timeout
				if (this.autoSaveTimeout) {
					clearTimeout(this.autoSaveTimeout);
				}
				
				// Debounce the regeneration to avoid too many API calls
				this.autoSaveTimeout = setTimeout(() => {
					this.regeneratePaymentLink();
				}, 1000); // Wait 1 second after last change
			}
		},
		regeneratePaymentLink() {
			if (this.regeneratingPaymentLink || !this.editingQuote) {
				return;
			}
			
			this.regeneratingPaymentLink = true;
			const quoteId = this.editingQuote.quote_id;
			
			// Prepare items for API call
			const itemsToSend = this.items.map(i => ({
				description: i.title,
				price: Math.round(i.price * 100), // Convert euros to cents for backend
				quantity: i.quantity,
				discount: i.discount || 0,
			}));
			
			const objectToSend = (this.object && this.object.length > 2) ? this.object : 'Preventivo Modificato';
			API.init().markQuoteAsCanceled(quoteId, (cancelResponse) => {
				if (cancelResponse.result === "OK") {

					API.init().superEmitInvoice(
						this.business?.business_id || 0, 
						0, // paid = 0 for quote
						objectToSend, 
						JSON.stringify(itemsToSend), 
						(response) => {
							this.regeneratingPaymentLink = false;
							
							if (response.result === "OK") {
								if (response.url) {
									// Update the payment link in the preview
									const oldLink = this.popupInvoiceLink;
									this.popupInvoiceLink = response.url;
									
									// Update the editingQuote with the new quote_id
									if (response.quote_id) {
										this.editingQuote.quote_id = parseInt(response.quote_id);
										this.currentQuoteId = parseInt(response.quote_id);
									}
									
									// Force re-render of QuotePreview component
									this.quotePreviewKey += 1;
								}
							} else if (response.details && response.details === "missing_data") {
								// Handle missing business data like in original
								this.popupInvoiceLink = response.url || '';
							}
						},
						true // forceIncomplete = true (same as original, NO quoteId to force new creation)
					);
				} else {
					this.regeneratingPaymentLink = false;
				}
			});
		},
		emitInvoice(callback) {
			// Save quote/invoice to database and show appropriate UI
			if (!this.paid) {
				// For unpaid quotes: save to database and show preview
				this.saveQuoteAndShowPreview();
				return;
			}
			
			// For paid invoices: continue with original logic
			
			let itemsToSend = this.items.map(i => {
				return {
					description: i.title,
					details: i.description || '',
					price: Math.round(i.price * 100),
					quantity: i.quantity,
					discount: i.discount || 0,
				}
			});
			this.loadingInvoice = true;
			const paid = this.paid ? 1 : 0;
			const quoteId = this.editingQuote?.quote_id || null;
			API.init().superEmitInvoice(this.business?.business_id || 0, paid, this.object || '', JSON.stringify(itemsToSend), (response) => {
				this.loadingInvoice = false;
				if (response.result && response.result === "OK") {
					if (this.paid) {
						this.$refs['success-generic'].openNotification();
						this.popupInvoiceTitle = this.getString('invoice_sent');
						this.popupInvoiceLink = response.url;
						this.popupInvoiceBody = this.getString('invoice_sent_warning');
						this.popupAction = this.closeDelay;
					} else {
						this.popupInvoiceTitle = this.getString('quote_created');
						this.popupInvoiceLink = response.url;
						this.popupInvoiceBody = "";
						this.popupAction = null;
						if (response.quote_id) {
							this.currentQuoteId = parseInt(response.quote_id);
						}
					}
					this.$refs['popup-invoice'].toggleStatus(true);
					if(callback) {
						callback();
					}
				} else if (response.details && response.details === "no_object") {
					this.$refs['error-no-object'].openNotification();
				} else if (response.details && response.details === "no_items") {
					this.$refs['error-no-items'].openNotification();
				} else if (response.details && response.details === "stripe_error") {
					this.$refs['error-stripe'].openNotification();
				} else if (response.details && response.details === "missing_data") {
					// Anche se mancano i dati aziendali, creiamo comunque il link di pagamento
					if (this.paid) {
						this.$refs['success-generic'].openNotification();
						this.popupInvoiceTitle = this.getString('invoice_sent');
						this.popupInvoiceLink = response.url || '';
						this.popupInvoiceBody = this.getString('invoice_sent_warning');
						this.popupAction = this.closeDelay;
					} else {
						this.popupInvoiceTitle = this.getString('quote_created');
						this.popupInvoiceLink = response.url || '';
						this.popupInvoiceBody = "";
						this.popupAction = null;
						if (response.quote_id) {
							this.currentQuoteId = parseInt(response.quote_id);
						}
					}
					this.$refs['popup-invoice'].toggleStatus(true);
					if(callback) {
						callback();
					}
					// Mostra un avviso ma non blocca la creazione
					this.$refs['error-missing-data'].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			}, true, quoteId);
		},
		saveQuoteAndShowPreview() {
			// Show preview first, then save changes to database
			if (this.items.length === 0) {
				this.$refs['error-no-items'].openNotification();
				return;
			}
			
			const quoteId = this.editingQuote?.quote_id || null;
			
			if (quoteId) {
				
				// Step 1: Show preview immediately
				this.quotePreviewActive = true;
				this.popupInvoiceLink = ''; // Use existing payment link
				// Close the modal/bottom sheet since we're editing an existing quote
				this.$emit('close');
				
				// Step 2: Save changes in background after preview is shown
				let itemsToSend = this.items.map(i => ({
					description: i.title,
					details: i.description || '',
					price: Math.round(i.price * 100), // Convert euros to cents for backend
					quantity: i.quantity,
					discount: i.discount || 0,
				}));

				// Use nextTick to ensure the preview opens first
				this.$nextTick(() => {

					const objectToSend = (this.object && this.object.length > 2) ? this.object : 'Preventivo Modificato';
					
					API.init().superEmitInvoice(
						this.business?.business_id || 0, 
						0, // paid = 0 for quote
						objectToSend, 
						JSON.stringify(itemsToSend), 
						(response) => {
							if (response.result === "OK") {
								
								// Verify the update by fetching the quote details again
								API.init().getSuperQuoteDetails(quoteId, (verifyResponse) => {

								});
							}
						}, 
						true, // force_incomplete flag
						quoteId // quote_id for update
					);
				});
			} else {
				let itemsToSend = this.items.map(i => ({
					description: i.title,
					details: i.description || '',
					price: Math.round(i.price * 100), // Convert euros to cents for backend
					quantity: i.quantity,
					discount: i.discount || 0,
				}));
				
				this.loadingInvoice = true;
				API.init().superEmitInvoice(this.business?.business_id || 0, 0, this.object || '', JSON.stringify(itemsToSend), (response) => {
					this.loadingInvoice = false;
					if (response.result && response.result === "OK") {
						this.popupInvoiceLink = response.url;
						if (response.quote_id) {
							this.currentQuoteId = parseInt(response.quote_id);
						}
						this.quotePreviewActive = true;
					} else if (response.details && response.details === "no_object") {
						this.$refs['error-no-object'].openNotification();
					} else if (response.details && response.details === "no_items") {
						this.$refs['error-no-items'].openNotification();
					} else if (response.details && response.details === "stripe_error") {
						this.$refs['error-stripe'].openNotification();
					} else if (response.details && response.details === "missing_data") {
						this.popupInvoiceLink = response.url || 'Link di pagamento non disponibile - completare i dati aziendali';
						if (response.quote_id) {
							this.currentQuoteId = parseInt(response.quote_id);
						}
						this.quotePreviewActive = true;
						this.$refs['error-missing-data'].openNotification();
					} else {
						this.$refs['error-generic'].openNotification();
					}
				}, true);
			}
		}
	},
	unmounted() {
		// Clear the autoSaveTimeout to prevent memory leaks
		if (this.autoSaveTimeout) {
			clearTimeout(this.autoSaveTimeout);
			this.autoSaveTimeout = null;
		}
	}
}
</script>

<style scoped lang="scss">
.items-container {
	display: flex;
	flex-direction: column;
	width: 100%;

	.section-toggle {
		display: flex;
		justify-content: center;
		margin-bottom: 16px;
	}

	.shortcuts-section {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.categories-tabs {
		display: flex;
		gap: 8px;
		margin-bottom: 12px;
		overflow-x: auto;
		@include hideScrollbar;

		.category-tab {
			padding: 8px 16px;
			border-radius: 20px;
			background-color: var(--color-bg-primary-2);
			color: var(--color-txt-2);
			font-size: 14px;
			font-weight: 500;
			cursor: pointer;
			transition: all 0.3s;
			white-space: nowrap;
			border: 1px solid transparent;

			&:hover {
				background-color: var(--color-bg-primary-3);
			}

			&.active {
				background-color: $primary;
				color: white;
				border-color: $primary;
			}
		}
	}

	.shortcuts-container {
		overflow-x: auto;
		white-space: nowrap;
		@include hideScrollbar;
		background-color: var(--color-bg-primary-2);
		box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
		border-radius: 13px;
		display: flex;
		padding: 8px;

		.shortcuts-wrapper {
			display: flex;
			align-items: center;
			gap: 6px;

			.shortcut {
				padding: 7px 12px;
				border-radius: 13px;
				transition: all 0.3s;
				border: 1px solid $primary;
				cursor: pointer;
				width: fit-content;
				display: flex;
				flex-direction: column;
				gap: 4px;
				height: 100%;

				.shortcut-title {
					color: $primary;
					font-size: 14px;
					font-weight: 500;
					max-width: 130px;
					@include truncateString(1);
				}

				.shortcut-price {
					font-size: 12px;
					color: var(--color-txt-1);
				}
			}
		}
	}

	.new-item-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		background-color: var(--color-bg-primary-2);
		padding: 20px;
		border-radius: 13px;
		border: 1px solid var(--color-border-1);

		.form-row {
			width: 100%;
			display: flex;
			gap: 20px;

			.left-column {
				flex: 1;
				display: flex;
				flex-direction: column;
				gap: 12px;

				.new-item-title {
					width: 100%;
				}

				.new-item-description {
					width: 100%;
				}
			}

			.right-column {
				flex: 1;
				display: flex;
				flex-direction: column;
				align-items: flex-end;
				gap: 12px;

				@media (max-width: 768px) {
					flex-direction: row;
					align-items: center;
					justify-content: space-between;
					gap: 8px;
					flex-wrap: wrap;
				}
			}
		}

		.add-button {
			width: 100%;
			height: 48px;
			font-size: 16px;
			font-weight: 600;
		}

		@media (max-width: 768px) {
			.form-row {
				flex-direction: column;
				
				.left-column, .right-column {
					width: 100%;
				}
			}
		}
	}

	.items-list {
		display: flex;
		flex-direction: column;
		width: 100%;
		margin-top: 12px;

		.item {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 10px 0;
			border-bottom: 1px solid var(--color-border-alpha);

			&:last-child {
				border-bottom: none;
			}

			.info {
				flex: 1;
				
				.item-title {
					font-size: 16px;
					font-weight: 500;
					color: $primary;
					@include truncateString;
				}

				.item-description {
					font-size: 13px;
					color: var(--color-txt-2);
					margin-top: 2px;
					margin-bottom: 4px;
				}

				.item-price {
					font-size: 14px;
					color: var(--color-txt-1);
				}
			}

			.controls {
				display: flex;
				gap: 12px;
				align-items: flex-end;

				.control-item {
					display: flex;
					flex-direction: column;

					.control-label {
						font-size: 12px;
						color: var(--color-txt-2);
						margin-bottom: 4px;
						font-weight: 500;
					}
				}
			}

		}
	}
}

.loader-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	min-height: 200px;

	.loader-text {
		margin-top: 16px;
		font-size: 14px;
		color: var(--color-txt-2);
		text-align: center;
	}
}


</style>
